import {
  type StyleFunctionProps,
  type StyleObjectNonMulti,
} from '@frontend/components/src/theme/styleConfigHelpers';

import { DEFAULT_PROPS } from './const';
import type {
  ButtonColorScheme,
  ButtonSize,
  ButtonStyleConfig,
  ButtonVariant,
} from './types';

const BASE_STYLE = {
  border: '1px solid',
  borderRadius: 'md',
  fontFamily: 'default',
  fontWeight: 'medium',
  textTransform: 'uppercase',
  transitionProperty: 'common',
  transitionDuration: 'normal',
  cursor: 'pointer',
  _disabled: {
    cursor: 'not-allowed',
  },
  _hover: {
    outline: 'none',
  },
  _focus: {
    boxShadow: 'focus',
  },
} as const;

const NON_LINK_BUTTON_SIZE_TO_TEXT_SIZE_MAP: Record<
  ButtonSize,
  StyleObjectNonMulti['fontSize']
> = {
  sm: 'sm',
  md: 'sm',
  lg: 'md',
};

/**
 * Takes the button's size and transforms it to the correct font size.
 * For text buttons, this size is simply the button size.
 * However, for all non-text buttons, the size has to be shifted down by one.
 *
 * @param size The button's size
 * @param variant The button's variant
 * @returns {*} An object containing the correct fontSize and lineHeight
 */
const getBaseSizeStyles = (
  size: ButtonSize,
  { variant }: StyleFunctionProps<ButtonVariant, ButtonColorScheme>,
): StyleObjectNonMulti => {
  const textSize =
    variant === 'link' ? size : NON_LINK_BUTTON_SIZE_TO_TEXT_SIZE_MAP[size];

  return { fontSize: textSize, lineHeight: textSize };
};

const SIZES: ButtonStyleConfig['sizes'] = {
  lg: (styleFunctionProps) => ({
    ...getBaseSizeStyles('lg', styleFunctionProps),
    px: 2,
    h: 5,
  }),
  md: (styleFunctionProps) => ({
    ...getBaseSizeStyles('md', styleFunctionProps),
    px: 1.5,
    h: 4,
  }),
  sm: (styleFunctionProps) => ({
    ...getBaseSizeStyles('sm', styleFunctionProps),
    px: 1,
    h: 3,
  }),
};

const COLOR_SCHEMES: {
  [key in ButtonColorScheme]: {
    primaryMain: string;
    primaryFeature: string;
    secondaryFeature: string;
    secondaryBorder: string;
    hover: string;
    active: string;
  };
} = {
  default: {
    primaryMain: 'interaction.main',
    primaryFeature: 'feature.onInteraction',
    secondaryFeature: 'feature.secondary',
    secondaryBorder: 'separator.border',
    hover: 'interaction.hover',
    active: 'interaction.active',
  },
  destructive: {
    primaryMain: 'interaction.destructive',
    primaryFeature: 'feature.onInteraction',
    secondaryFeature: 'interaction.destructive',
    secondaryBorder: 'interaction.destructive',
    hover: 'interaction.destructiveHover',
    active: 'interaction.destructiveActive',
  },
  suggestion: {
    primaryMain: 'interaction.suggestion',
    primaryFeature: 'feature.onInteraction',
    secondaryFeature: 'interaction.suggestion',
    secondaryBorder: 'interaction.suggestion',
    hover: 'interaction.suggestionHover',
    active: 'interaction.suggestionActive',
  },
  onInteraction: {
    primaryMain: 'feature.onInteraction',
    primaryFeature: 'interaction.main',
    secondaryFeature: 'interaction.main',
    secondaryBorder: 'interaction.main',
    hover: 'feature.onInteraction',
    active: 'feature.onInteraction',
  },
};

const VARIANTS: ButtonStyleConfig['variants'] = {
  primary: ({ colorScheme }) => {
    const schemeColors = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.default;
    return {
      bg: schemeColors.primaryMain,
      borderColor: schemeColors.primaryMain,
      color: schemeColors.primaryFeature,
      _hover: {
        borderColor: schemeColors.hover,
        bg: schemeColors.hover,
        _disabled: {
          borderColor: 'interaction.disabled',
          bg: 'interaction.disabled',
        },
      },
      _active: {
        borderColor: schemeColors.active,
        bg: schemeColors.active,
      },
      _disabled: {
        borderColor: 'interaction.disabled',
        bg: 'interaction.disabled',
      },
    };
  },
  secondary: ({ colorScheme }) => {
    const schemeColors = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.default;
    return {
      bg: 'page.surface',
      borderColor: schemeColors.secondaryBorder,
      color: schemeColors.secondaryFeature,
      _hover: {
        borderColor: schemeColors.hover,
        color: schemeColors.hover,
        _disabled: {
          borderColor: 'separator.border',
          bg: 'interaction.surfaceDisabled',
          color: 'interaction.disabled',
        },
      },
      _active: {
        borderColor: schemeColors.active,
        color: schemeColors.active,
      },
      _disabled: {
        borderColor: 'separator.border',
        bg: 'interaction.surfaceDisabled',
        color: 'interaction.disabled',
      },
    };
  },
  link: ({ colorScheme }) => {
    const schemeColors = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.default;
    return {
      padding: 0,
      height: 'auto',
      verticalAlign: 'baseline',
      color: schemeColors.primaryMain,
      border: 'none',
      textTransform: 'none',

      _hover: {
        color: schemeColors.hover,
        textDecoration: 'underline',

        _disabled: {
          color: 'interaction.disabled',
          textDecoration: 'none',
        },
      },

      _active: {
        color: schemeColors.active,
      },

      _disabled: {
        color: 'interaction.disabled',
      },
    };
  },
};

export const BUTTON_STYLE_CONFIG: ButtonStyleConfig = {
  baseStyle: BASE_STYLE,
  sizes: SIZES,
  variants: VARIANTS,
  defaultProps: DEFAULT_PROPS,
};
